// node_modules
import React, { FC, useContext, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrashAlt } from "@fortawesome/free-solid-svg-icons";
// Types
import { TEnvironmentVariableDTO } from "Types";
// Components
import { BorderedIconedTextblock, FindestEditableLabelledCheckbox, FindestSelectSearchPriority } from "Components";
import { SynonymsButton } from "../Synonyms/SynonymsButton";
import { Synonyms } from "../Synonyms/Synonyms";
// Enums
import { SearchPriorityEnum, SynonymTypeEnum } from "Enums";
// styles
import styles from "./environmentVariable.module.scss";
import variables from "styles/colors.module.scss";
import { FeatureToggleConstants } from "Constants";
import { UserContext } from "Providers";

type TEnvironmentVariableProps = {
    environment: TEnvironmentVariableDTO,
    handleDeleteEnvironment: (id: number) => void,
    handleEditEnvironment: (id: number, text: string) => void
    handleChangeEnvironmentSearchPriority: (id: number, environmentNewSearchPriority: SearchPriorityEnum) => void
}

export const EnvironmentVariable: FC<TEnvironmentVariableProps> = ({environment, handleDeleteEnvironment, handleEditEnvironment, handleChangeEnvironmentSearchPriority}: TEnvironmentVariableProps) => {
    
    // Context
    const { isCurrentUserFindest } = useContext(UserContext);
    
    // const
    const isSearchTermGroupsEnabled = FeatureToggleConstants.SynonymGroups && isCurrentUserFindest;
    
    // State
    const [isSynonymsSectionShown, setIsSynonymsSectionShown] = useState<boolean>(false);
    const [clickedElement, setClickedElement] = useState<{ element: EventTarget } | undefined>(undefined);

    //Logic
    const handleOnIsEnvironmentEnabledOnChange = (isEnvironmentEnabled: boolean) => {
        // call parent's is keyword enabled handler
        handleChangeEnvironmentSearchPriority(environment.id, isEnvironmentEnabled ? SearchPriorityEnum.Should : SearchPriorityEnum.Disabled);
        // if the keyword is disabled, hide synonyms section
        if (!isEnvironmentEnabled) setIsSynonymsSectionShown(false);
    };

    const handleOnEnvironmentSearchPriorityOnChange = (newEnvironmentSearchPriority: SearchPriorityEnum) => {
        // set parent props keyword search priority
        handleChangeEnvironmentSearchPriority(environment.id, newEnvironmentSearchPriority);
    };

    const handleDeleteKeywordOnClick = (): void => {
        // call parent's delete keyword handler
        handleDeleteEnvironment(environment.id);
    };

    const handleEnvironmentChange = (newLabelValue: string, _oldLabelValue: string): void => {
        handleEditEnvironment(environment.id, newLabelValue);
    };

    const openSynonymsSection = (event: React.MouseEvent): void => { 
        // if the keyword is enabled, show synonyms section
        if (environment.searchPriority !== SearchPriorityEnum.Disabled) setIsSynonymsSectionShown(true);
        setClickedElement({ element: event.target });
    };

    const closeSynonymsSection = (): void => {
        // hide synonyms section
        setIsSynonymsSectionShown(false);
    };

    const setClickedElementonFocus = (event: React.FocusEvent): void => {
        setClickedElement({ element: event.target });
    };

    return (
        <div className={styles.findestKeywordContainer}>
            <div className={environment.searchPriority === SearchPriorityEnum.Disabled ? [styles.findestKeyword, styles.findestKeywordDisabled].join(" ") : styles.findestKeyword}>
                <div style={{display: "flex", alignItems: "center", width: "100%"}}>
                    {isSearchTermGroupsEnabled && environment.termGroupName ? <BorderedIconedTextblock text="group" backgroundColor={variables.findestPurple} color="white" /> : null }
                    <FindestEditableLabelledCheckbox label={isSearchTermGroupsEnabled && environment.termGroupName ? environment.termGroupName : environment.environment} placeholder={"Keyword"} 
                        checked={environment.searchPriority !== SearchPriorityEnum.Disabled ? true : false} 
                        isReadonly={isSearchTermGroupsEnabled && environment.termGroupName ? true : false}
                        onCheckboxChange={handleOnIsEnvironmentEnabledOnChange} 
                        onLabelChange={handleEnvironmentChange} 
                        onTextFocus={setClickedElementonFocus} />
                    <div style={{display: environment.searchPriority === SearchPriorityEnum.Disabled ? "none" : "block"}}>
                        <SynonymsButton 
                            isSynonymsSectionShown={isSynonymsSectionShown} 
                            synonymsCount={isSearchTermGroupsEnabled && environment.termGroupName ? environment.synonyms.length + 1: environment.synonyms.length}
                            handleOpenSynonymsSection={openSynonymsSection}
                            handleCloseSynonymsSection={closeSynonymsSection}
                            />
                    </div>
                </div>
                <div style={{display: "flex", marginLeft: "0.5rem", alignItems: "center"}}>
                    {environment.searchPriority !== SearchPriorityEnum.Disabled ? <FindestSelectSearchPriority searchPriority={environment.searchPriority} onChange={handleOnEnvironmentSearchPriorityOnChange}/> : <></>}
                    {environment.searchPriority !== SearchPriorityEnum.Disabled ? <div title="Delete keyword"><FontAwesomeIcon className={styles.deleteButton} icon={faTrashAlt} onClick={handleDeleteKeywordOnClick} /></div> : <></>}
                </div>
            </div>
            <div style={{display: isSynonymsSectionShown ? "block" : "none"}} className={styles.findestSynoymsContainer} >
                <Synonyms synonyms={environment.synonyms} synonymType={SynonymTypeEnum.Environment} parentElementId={environment.id} 
                    parentElementText={environment.environment} isOpen={isSynonymsSectionShown} clickedParentElement={clickedElement}
                    mainTermGroupValue={environment.termGroupName ? environment.environment : undefined}
                    isPartOfTermGroup={isSearchTermGroupsEnabled && !!environment.termGroupName} />
            </div>    
        </div>
    );
};